import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Upload, Mic, PenLine, Eye, Trash2, Play, ChevronLeft, FileVideo, Check, Loader2, Globe, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPodcastStudio() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileRef = useRef(null);

  const headers = () => {
    const h = {};
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  const jsonHeaders = () => ({ ...headers(), 'Content-Type': 'application/json' });

  // Fetch episodes
  useEffect(() => {
    fetchEpisodes();
  }, [token]);

  // Poll for processing status
  useEffect(() => {
    const processing = episodes.find(e => e.processing);
    if (!processing) return;
    const interval = setInterval(fetchEpisodes, 3000);
    return () => clearInterval(interval);
  }, [episodes]);

  const fetchEpisodes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/podcast/episodes`, { headers: headers(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEpisodes(data.episodes || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // Upload video
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name.replace(/\.[^/.]+$/, ''));

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/podcast/upload`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
      };

      xhr.onload = async () => {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          toast.success(`Uploaded: ${data.title} (${data.file_size_mb}MB)`);
          await fetchEpisodes();
          setSelected(data.episode_id);
        } else {
          const err = JSON.parse(xhr.responseText);
          toast.error(err.detail || 'Upload failed');
        }
        setUploading(false);
        setUploadProgress(0);
      };

      xhr.onerror = () => { toast.error('Upload failed'); setUploading(false); };
      xhr.send(formData);
    } catch (e) {
      toast.error('Upload error');
      setUploading(false);
    }
  };

  // Transcribe
  const handleTranscribe = async (episodeId) => {
    setTranscribing(true);
    try {
      const res = await fetch(`${API_URL}/api/podcast/transcribe/${episodeId}`, {
        method: 'POST', headers: headers(), credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Transcription started — this may take a minute...');
      } else {
        toast.error(data.detail || 'Transcription failed');
      }
    } catch (e) { toast.error('Transcription error'); }
    setTranscribing(false);
  };

  // AI Rewrite
  const handleRewrite = async (episodeId, mode) => {
    setRewriting(true);
    try {
      const res = await fetch(`${API_URL}/api/podcast/rewrite/${episodeId}`, {
        method: 'POST', headers: jsonHeaders(), credentials: 'include',
        body: JSON.stringify({ mode }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`${mode === 'article' ? 'Article' : 'Commentary'} generation started...`);
      } else {
        toast.error(data.detail || 'Rewrite failed');
      }
    } catch (e) { toast.error('Rewrite error'); }
    setRewriting(false);
  };

  // Update episode
  const handleUpdate = async (episodeId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/podcast/episodes/${episodeId}`, {
        method: 'PUT', headers: jsonHeaders(), credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        toast.success('Updated');
        await fetchEpisodes();
      }
    } catch (e) { toast.error('Update failed'); }
  };

  // Delete
  const handleDelete = async (episodeId) => {
    if (!window.confirm('Delete this episode permanently?')) return;
    try {
      const res = await fetch(`${API_URL}/api/podcast/episodes/${episodeId}`, {
        method: 'DELETE', headers: headers(), credentials: 'include',
      });
      if (res.ok) {
        toast.success('Deleted');
        setSelected(null);
        await fetchEpisodes();
      }
    } catch (e) { toast.error('Delete failed'); }
  };

  const ep = selected ? episodes.find(e => e.episode_id === selected) : null;

  return (
    <div className="min-h-screen bg-[#060503] text-amber-100">
      {/* Header */}
      <nav className="flex items-center justify-between px-6 py-4 border-b border-amber-900/20 bg-[#060503]/90 backdrop-blur-sm">
        <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-amber-600 hover:text-amber-400 transition-colors text-sm" data-testid="back-to-admin">
          <ChevronLeft className="w-4 h-4" /> Admin Dashboard
        </button>
        <h1 className="text-base font-serif tracking-[0.2em] text-amber-500/80 uppercase">Podcast Studio</h1>
        <div className="w-24" />
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left: Episode List + Upload */}
          <div className="lg:col-span-1">
            {/* Upload */}
            <div className="mb-6">
              <input type="file" ref={fileRef} accept="video/*" onChange={handleUpload} className="hidden" data-testid="video-file-input" />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full py-6 rounded-xl border-2 border-dashed border-amber-800/30 hover:border-amber-600/50 transition-all flex flex-col items-center gap-2 group"
                style={{ background: 'rgba(15,10,4,0.6)' }}
                data-testid="upload-video-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                    <span className="text-sm text-amber-400 font-mono">{uploadProgress}%</span>
                    <div className="w-48 h-1.5 rounded-full bg-amber-900/30 overflow-hidden">
                      <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-amber-700 group-hover:text-amber-500 transition-colors" />
                    <span className="text-sm text-amber-500/70 font-serif">Upload Video</span>
                    <span className="text-[10px] text-amber-700/50 font-mono">MP4, MOV, WEBM</span>
                  </>
                )}
              </button>
            </div>

            {/* Episodes list */}
            <div className="space-y-2">
              <p className="text-xs font-mono text-amber-600/50 tracking-wider uppercase mb-3">Episodes ({episodes.length})</p>
              {loading ? (
                <div className="text-center py-8 text-amber-700/50 font-serif text-sm">Loading...</div>
              ) : episodes.length === 0 ? (
                <div className="text-center py-8 text-amber-700/40 font-serif text-sm">No episodes yet. Upload your first video above.</div>
              ) : (
                episodes.map(e => (
                  <button
                    key={e.episode_id}
                    onClick={() => setSelected(e.episode_id)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selected === e.episode_id
                        ? 'border-amber-600/40 bg-amber-900/15'
                        : 'border-amber-900/15 hover:border-amber-800/30 bg-[#0a0804]'
                    }`}
                    data-testid={`episode-item-${e.episode_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <FileVideo className="w-5 h-5 text-amber-700/50 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-serif text-amber-200/80 truncate">{e.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <StatusBadge status={e.status} />
                          <span className="text-[9px] text-amber-700/40 font-mono">{e.file_size_mb}MB</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right: Episode Detail / Workspace */}
          <div className="lg:col-span-2">
            {!ep ? (
              <div className="flex items-center justify-center h-96 rounded-xl border border-amber-900/15 bg-[#0a0804]">
                <div className="text-center">
                  <FileVideo className="w-12 h-12 text-amber-800/30 mx-auto mb-3" />
                  <p className="text-amber-600/40 font-serif text-sm">Select an episode or upload a new video</p>
                </div>
              </div>
            ) : (
              <EpisodeWorkspace
                episode={ep}
                onTranscribe={handleTranscribe}
                onRewrite={handleRewrite}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                transcribing={transcribing}
                rewriting={rewriting}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    uploaded: { bg: 'bg-blue-900/30', text: 'text-blue-400', border: 'border-blue-700/30', label: 'Uploaded' },
    transcribed: { bg: 'bg-amber-900/30', text: 'text-amber-400', border: 'border-amber-700/30', label: 'Transcribed' },
    rewritten: { bg: 'bg-purple-900/30', text: 'text-purple-400', border: 'border-purple-700/30', label: 'Rewritten' },
    published: { bg: 'bg-emerald-900/30', text: 'text-emerald-400', border: 'border-emerald-700/30', label: 'Published' },
  };
  const s = styles[status] || styles.uploaded;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono tracking-wider ${s.bg} ${s.text} border ${s.border}`} data-testid={`status-${status}`}>
      {s.label}
    </span>
  );
}

function EpisodeWorkspace({ episode, onTranscribe, onRewrite, onUpdate, onDelete, transcribing, rewriting }) {
  const [tab, setTab] = useState('overview');
  const [editTitle, setEditTitle] = useState(episode.title);
  const [editDesc, setEditDesc] = useState(episode.description || '');
  const [editSeries, setEditSeries] = useState(episode.series || '');
  const [editArticle, setEditArticle] = useState(episode.article_rewrite || '');
  const [editCommentary, setEditCommentary] = useState(episode.commentary_rewrite || '');

  useEffect(() => {
    setEditTitle(episode.title);
    setEditDesc(episode.description || '');
    setEditSeries(episode.series || '');
    setEditArticle(episode.article_rewrite || '');
    setEditCommentary(episode.commentary_rewrite || '');
    // Auto-switch to transcript tab if just transcribed
    if (episode.transcript && tab === 'overview') setTab('transcript');
  }, [episode.episode_id, episode.status]);

  const tabs = [
    { key: 'overview', label: 'Overview', icon: Eye },
    { key: 'transcript', label: 'Transcript', icon: Mic, disabled: !episode.transcript },
    { key: 'article', label: 'Article', icon: PenLine, disabled: !episode.article_rewrite },
    { key: 'commentary', label: 'Commentary', icon: PenLine, disabled: !episode.commentary_rewrite },
  ];

  return (
    <div className="rounded-xl border border-amber-900/20 bg-[#0a0804] overflow-hidden">
      {/* Video Preview */}
      <div className="relative aspect-video bg-black">
        <video
          src={`${API_URL}/api/podcast/stream/${episode.episode_id}`}
          controls
          className="w-full h-full"
          data-testid="video-player"
        />
      </div>

      {/* Title & Meta */}
      <div className="px-5 py-4 border-b border-amber-900/15">
        <input
          value={editTitle}
          onChange={e => setEditTitle(e.target.value)}
          onBlur={() => editTitle !== episode.title && onUpdate(episode.episode_id, { title: editTitle })}
          className="w-full text-lg font-serif text-amber-200 bg-transparent border-none outline-none placeholder:text-amber-700/30"
          placeholder="Episode title..."
          data-testid="edit-title"
        />
        <div className="flex items-center gap-3 mt-2">
          <StatusBadge status={episode.status} />
          <span className="text-[10px] text-amber-600/40 font-mono">{episode.file_size_mb}MB</span>
          <span className="text-[10px] text-amber-600/40 font-mono">{new Date(episode.created_at).toLocaleDateString()}</span>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <input
            value={editSeries}
            onChange={e => setEditSeries(e.target.value)}
            onBlur={() => editSeries !== (episode.series || '') && onUpdate(episode.episode_id, { series: editSeries })}
            className="px-3 py-1.5 rounded-lg bg-[#060503] border border-amber-900/20 text-sm text-amber-300 font-serif placeholder:text-amber-800/30 flex-1 outline-none focus:border-amber-700/40"
            placeholder="Series name..."
            data-testid="edit-series"
          />
          <textarea
            value={editDesc}
            onChange={e => setEditDesc(e.target.value)}
            onBlur={() => editDesc !== (episode.description || '') && onUpdate(episode.episode_id, { description: editDesc })}
            className="px-3 py-1.5 rounded-lg bg-[#060503] border border-amber-900/20 text-sm text-amber-300 font-serif placeholder:text-amber-800/30 flex-1 outline-none focus:border-amber-700/40 resize-none"
            placeholder="Description..."
            rows={1}
            data-testid="edit-description"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-5 py-3 flex flex-wrap gap-2 border-b border-amber-900/15">
        {episode.processing && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-900/15 border border-blue-700/30 text-blue-400 text-[11px] font-mono" data-testid="processing-indicator">
            <Loader2 className="w-3 h-3 animate-spin" />
            {episode.processing === 'transcribing' ? 'Transcribing audio...' :
             episode.processing.startsWith('rewriting') ? `Generating ${episode.processing.split('_')[1]}...` : 'Processing...'}
          </div>
        )}
        {episode.processing_error && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-900/15 border border-red-700/30 text-red-400 text-[11px] font-mono" data-testid="processing-error">
            {episode.processing_error}
          </div>
        )}
        {!episode.transcript && !episode.processing && (
          <ActionButton onClick={() => onTranscribe(episode.episode_id)} loading={transcribing} icon={Mic} label="Transcribe" testId="btn-transcribe" />
        )}
        {episode.transcript && !episode.processing && (
          <>
            <ActionButton onClick={() => onRewrite(episode.episode_id, 'article')} loading={rewriting} icon={PenLine} label="Generate Article" testId="btn-rewrite-article" />
            <ActionButton onClick={() => onRewrite(episode.episode_id, 'commentary')} loading={rewriting} icon={PenLine} label="Generate Commentary" testId="btn-rewrite-commentary" />
          </>
        )}
        {(episode.article_rewrite || episode.commentary_rewrite) && episode.status !== 'published' && (
          <ActionButton
            onClick={() => onUpdate(episode.episode_id, { status: 'published' })}
            icon={Globe} label="Publish" testId="btn-publish"
            className="bg-emerald-900/20 border-emerald-700/30 text-emerald-400 hover:bg-emerald-900/30"
          />
        )}
        {episode.status === 'published' && (
          <ActionButton
            onClick={() => onUpdate(episode.episode_id, { status: 'rewritten' })}
            icon={EyeOff} label="Unpublish" testId="btn-unpublish"
            className="bg-amber-900/20 border-amber-700/30 text-amber-400 hover:bg-amber-900/30"
          />
        )}
        <button
          onClick={() => onUpdate(episode.episode_id, { show_video_public: !episode.show_video_public })}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-mono border transition-colors ${
            episode.show_video_public
              ? 'bg-blue-900/20 border-blue-700/30 text-blue-400'
              : 'bg-slate-900/20 border-slate-700/30 text-slate-400'
          }`}
          data-testid="toggle-video-public"
        >
          {episode.show_video_public ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          Video {episode.show_video_public ? 'Visible' : 'Hidden'}
        </button>
        <button
          onClick={() => onDelete(episode.episode_id)}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-mono border border-red-900/30 text-red-500/70 hover:bg-red-900/15 transition-colors"
          data-testid="btn-delete-episode"
        >
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-amber-900/15">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => !t.disabled && setTab(t.key)}
            disabled={t.disabled}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-mono tracking-wider transition-colors ${
              tab === t.key ? 'text-amber-300 border-b-2 border-amber-500' : t.disabled ? 'text-amber-900/30 cursor-not-allowed' : 'text-amber-600/50 hover:text-amber-500'
            }`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon className="w-3 h-3" /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-5" style={{ minHeight: '200px', maxHeight: '50vh', overflowY: 'auto' }}>
        {tab === 'overview' && (
          <div className="text-center py-8">
            <p className="text-amber-500/60 font-serif text-sm mb-4">
              {!episode.transcript
                ? 'Click "Transcribe" to extract the spoken words from this video'
                : !episode.article_rewrite && !episode.commentary_rewrite
                  ? 'Transcript ready! Generate an Article or Commentary rewrite'
                  : 'Content is ready. Review and publish when satisfied.'}
            </p>
            <div className="flex items-center justify-center gap-4">
              <StepIndicator done={!!episode.transcript} label="Transcribed" step={1} />
              <div className="w-8 h-px bg-amber-900/20" />
              <StepIndicator done={!!episode.article_rewrite || !!episode.commentary_rewrite} label="Rewritten" step={2} />
              <div className="w-8 h-px bg-amber-900/20" />
              <StepIndicator done={episode.status === 'published'} label="Published" step={3} />
            </div>
          </div>
        )}

        {tab === 'transcript' && episode.transcript && (
          <div className="prose prose-invert prose-amber max-w-none">
            <pre className="whitespace-pre-wrap text-sm font-serif text-amber-300/80 leading-relaxed bg-transparent p-0" data-testid="transcript-text">
              {episode.transcript}
            </pre>
          </div>
        )}

        {tab === 'article' && (
          <div>
            <textarea
              value={editArticle}
              onChange={e => setEditArticle(e.target.value)}
              onBlur={() => editArticle !== (episode.article_rewrite || '') && onUpdate(episode.episode_id, { article_rewrite: editArticle })}
              className="w-full min-h-[300px] bg-transparent border-none outline-none text-sm font-serif text-amber-300/80 leading-relaxed resize-y placeholder:text-amber-800/30"
              placeholder="Article content will appear here..."
              data-testid="edit-article"
            />
          </div>
        )}

        {tab === 'commentary' && (
          <div>
            <textarea
              value={editCommentary}
              onChange={e => setEditCommentary(e.target.value)}
              onBlur={() => editCommentary !== (episode.commentary_rewrite || '') && onUpdate(episode.episode_id, { commentary_rewrite: editCommentary })}
              className="w-full min-h-[300px] bg-transparent border-none outline-none text-sm font-serif text-amber-300/80 leading-relaxed resize-y placeholder:text-amber-800/30"
              placeholder="Commentary content will appear here..."
              data-testid="edit-commentary"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function ActionButton({ onClick, loading, icon: Icon, label, testId, className = '' }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-mono border transition-colors disabled:opacity-40 ${
        className || 'bg-amber-900/15 border-amber-800/25 text-amber-400 hover:bg-amber-900/25'
      }`}
      data-testid={testId}
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      {label}
    </button>
  );
}

function StepIndicator({ done, label, step }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center border ${
        done ? 'bg-emerald-900/30 border-emerald-600/40' : 'bg-[#0d0a05] border-amber-900/20'
      }`}>
        {done ? <Check className="w-4 h-4 text-emerald-400" /> : <span className="text-xs text-amber-700/40 font-mono">{step}</span>}
      </div>
      <span className={`text-[9px] font-mono ${done ? 'text-emerald-400/70' : 'text-amber-700/30'}`}>{label}</span>
    </div>
  );
}
